"use strict";

import React from "react";
import { findDOMNode } from "react-dom";
import { Collapse, Modal, Button } from "react-bootstrap";
import { noteContextUrl, noteContextLabel } from "./data_helpers.js";
import {
  handyArray,
  scoreIsMissing,
  linearTransform,
  verboseTimestamp,
  reduceToObject,
  simpleTitleCase,
} from "./shared.js";
import { IndividualGraphicRep } from "./rep_graphics.js";
import { GlyphCollapseToggle } from "./icons.js";

// same as in __base.html, but usable from pure-JS rendering
export let RankStyles = ({ allRanks }) => (
  <style>
    {allRanks.map(
      (rank) => `
    .rank-${rank.code} { fill: ${rank.color}; color: ${rank.color}; }
    .rank-${rank.code}-ring { stroke: ${rank.webapp_darker_color}; border-color: ${rank.webapp_darker_color}; }
    .rank-${rank.code}-line { stroke: ${rank.axis_color}; }
    .rank-${rank.code}-cell { background-color: ${rank.color}; fill: ${rank.color}; stroke: ${rank.text_color}; }
    .rank-${rank.code}-text { fill: ${rank.text_color}; color: ${rank.text_color}; }

    .rank-blend.start-rank-${rank.code} stop:first-of-type { stop-color: ${rank.color}; }
    .rank-blend.final-rank-${rank.code} stop:last-of-type { stop-color: ${rank.color}; }

    /* BACKWARDS COMPATIBILITY: used by overall.organization_statement database snippet */
    /* NOTE: in the classic version this was/is an actual highlight, but here we only want colored text */
    .rank${rank.code}-highlight { fill: ${rank.color}; color: ${rank.color}; }
  `,
    )}
  </style>
);

const roundNum = (n) => Math.round(n).toFixed(0);

export let RawScore = ({ result, Wrapper = "span", missing_label = "N/A" }) =>
  scoreIsMissing(result) ? (
    <Wrapper className="raw-score missing">{missing_label}</Wrapper>
  ) : (
    <Wrapper className="raw-score integer">{roundNum(result.score)}</Wrapper>
  );

export let SummarizedScore = ({ result }) => (
  <span className={"score score-summary rank-" + result.code}>
    {result.rank.label}
  </span>
);

export let LongSummarizedScore = ({ item, result }) => (
  <span className={`score long score-summary rank-${result.code}`}>
    {item._alias === "5e_overall"
      ? result.rank.overall_long_label
      : result.rank.long_label}
  </span>
);

export let ComboScore = ({ item, result, longSummary = false }) => {
  // only shows label when score unavailable
  let summary = longSummary ? (
    <LongSummarizedScore item={item} result={result} />
  ) : (
    <SummarizedScore result={result} />
  );
  let hideRawScore = item._alias || scoreIsMissing(result);
  return hideRawScore ? (
    summary
  ) : (
    <span className="combo-score">
      <RawScore result={result} /> <SummarizedScore result={result} />
    </span>
  );
};

export let ChangeDisplay = ({
  result,
  Wrapper = "span",
  pos = "+",
  neg = "-",
}) =>
  result._change == null ? (
    <Wrapper className="relative-change missing">N/A</Wrapper>
  ) : (
    <Wrapper className="relative-change">
      {result._change < 0 ? neg : pos} {roundNum(Math.abs(result._change))}
    </Wrapper>
  );

export let IntroHeadline = ({
  target,
  survey,
  overall_organization_statement,
  leadIn = "For",
  WRAPPER = "h1",
}) =>
  target.is_group ? null : (
    <WRAPPER
      dangerouslySetInnerHTML={{
        __html: `${leadIn} ${survey.year}, ${overall_organization_statement}.`,
      }}
    />
  );

export let IntroParagraph = ({ target, landing_summary, overall_product }) =>
  target.is_group ? (
    <p>
      A group’s performance is an aggregate of the individual schools’
      performance on each of the {overall_product}. Group reports show the
      distribution of performance across schools, trends in specific Essentials
      and Measures, and identifies strengths and areas for improvement.
    </p>
  ) : landing_summary ? (
    <p dangerouslySetInnerHTML={landing_summary} />
  ) : (
    <p>
      The overall performance score is comprised of each of the{" "}
      {overall_product} scores. Schools that are at or above benchmark on 3 or
      more essentials are 10 times more likely to improve than schools that are
      below the benchmark.
    </p>
  );

export let RespondentLabel = ({ subject, subjectLabels }) => (
  <span className="respondent-label">
    {simpleTitleCase(subjectLabels[subject])}
  </span>
);

export let ParentCell = ({ item: d, survey }) => (
  <div className="parent-cell">
    <IndividualGraphicRep survey={survey} item={d._parent} />
    <span className="parent-name">{d._parent.name}</span>
  </div>
);

export let RankHighlight = ({ result }) => (
  <span className={`rank${result.rank.code}-highlight`}>
    {simpleTitleCase(result.rank.label)}
  </span>
);

// c.f. react-bootstrap/src/CloseButton.js (non-exported…)
export let CloseButton = ({ label, onClick }) => (
  <button type="button" className="close" onClick={onClick}>
    <span aria-hidden="true">&times;</span>
    <span className="sr-only">{label || "Close"}</span>
  </button>
);

export let WrappedText = ({
  children: text,
  x = 0,
  config: { MAX_CHARS = 8, ALIGN = "top" } = {},
  ...props
}) => {
  // HACK: `children` property is technically an opaque data structure. see https://github.com/facebook/react/issues/9255
  if (Array.isArray(text)) text = text.join("");

  // TODO: this may someday need a better wrapping algorithm, since not all code units advance equally
  //       see e.g. https://bl.ocks.org/mbostock/7555321 (port similar to a stateful React component?)
  let lines = handyArray([""]);
  text.split(/\s/).forEach((s) => {
    if (lines.last) {
      // before each additional word…
      if (lines.last.length + s.length >= MAX_CHARS) {
        // NOTE: the `=` in comparison compensates for the space character
        // …either advance to the next line…
        lines.push("");
      } else if (lines.last) {
        // …or replace whitespace between.
        lines.last += " ";
      }
    }
    lines.last += s;
  });
  let initialOffset;
  switch (ALIGN) {
    case "top":
      initialOffset = 1;
      break;
    case "center":
      initialOffset = 1 - lines.length / 2;
      break;
    case "bottom":
      initialOffset = 1 - lines.length;
      break;
    default:
      throw Error("Unsupported alignment!");
  }

  // for semantic legibility, we need to add back the spaces (without breaking centering)
  let texts = [].concat(...lines.map((str) => [str, " "])).slice(0, -1);
  return (
    <text {...props}>
      {texts.map((s, i) =>
        i % 2 ? (
          <tspan key={i} dx="-1ex">
            {s}
          </tspan>
        ) : (
          <tspan key={i} x={x} dy={`${i ? 1 : initialOffset}em`}>
            {s}
          </tspan>
        ),
      )}
    </text>
  );
};

// helper used by FrequencyGraph/FrequencyBar
const _yClass = (y) => (y.has_measures ? (y._prior ? "prior" : "") : "recon");

let FrequencyBar = ({
  d,
  w,
  h,
  x,
  years,
  comparisonObject,
  showBenchmarks,
}) => {
  let P = 5;
  let BP = 5;

  let _vTuple = (v, cn) =>
    v == null ? [v, `missing ${cn}`, "--"] : [v, cn, `${roundNum(v)}`];

  let subbars = years.map((year) =>
    _vTuple(d.percents[year._item_index], _yClass(year)),
  );
  if (comparisonObject) {
    // ~HACK: BEL comparison data is stored quite differently than 5E item responses — we match it up here.
    let v = comparisonObject.items[d.item_slug][d.choice_index];
    subbars.push(_vTuple(v, "comparison"));
  }

  let pw = w + BP;
  let bw = pw / subbars.length - BP;
  let xScale = linearTransform([0, subbars.length], [x - pw / 2, x + pw / 2]);
  let yScale = linearTransform([0, 100], [h, 0 + P + 10]);
  let y0 = yScale(0);

  return (
    <g className="frequency-bar">
      {subbars.map(([v, cn], i) => {
        if (v != null) {
          let [x, y] = [xScale(i + 0.5), yScale(v)];
          return (
            <rect
              key={`bar${i}`}
              className={`percent-bar ${cn}`}
              x={x - bw / 2}
              width={bw}
              y={y}
              height={Math.max(1, y0 - y)}
            />
          );
        }
      })}
      {showBenchmarks && d.benchmark != null ? (
        <line
          className="benchmark-line"
          x1={x - w / 2}
          x2={x + w / 2}
          y1={yScale(d.benchmark)}
          y2={yScale(d.benchmark)}
        />
      ) : null}
      {subbars.map(([v, cn, label], i) => {
        let [x, y] = [xScale(i + 0.5), yScale(v || 0)];
        return (
          <text
            key={`lbl${i}`}
            className={`percent-label ${cn}`}
            textAnchor="middle"
            x={x}
            y={y - P}
          >
            {label}
          </text>
        );
      })}
      <WrappedText
        className="response-label"
        textAnchor="middle"
        x={x}
        y={yScale(0) + P}
        config={{ MAX_CHARS: 10 }}
      >
        {d.response} (%)
      </WrappedText>
    </g>
  );
};

export let FrequencyGraph = ({
  item,
  comparisonObject,
  survey,
  years,
  showBenchmarks,
  showLegend,
  newCultivateStyle,
}) => {
  let W = 450;
  let H = 165;
  let P = 20;
  let BH = 100; // "bar height"
  let BW = 50; // "bar width"

  if (newCultivateStyle) {
    W = 645;
    H = 150;
    BH = 85;
    BW = 55;
  }

  let xScale = linearTransform([0, item._responses.length], [0 + P, W - P]);
  function hasNoData(y) {
    return item._responses.every((r) => r.percents[y._item_index] == null);
  }
  return (
    <div className="frequency-graph-group">
      <svg
        className="frequency-graph"
        viewBox={[0, 0, W, H].join(" ")}
        width={W}
        height={H}
      >
        <line className="baseline" x1={0} x2={W} y1={BH} y2={BH} />
        {item._responses.map((d, i) => (
          <FrequencyBar
            key={i}
            d={d}
            w={BW}
            h={BH}
            x={xScale(i + 0.5)}
            {...{ years, comparisonObject, showBenchmarks }}
          />
        ))}
      </svg>
      {showLegend ? (
        <ul className="legend">
          {years.map((year, i) => (
            <li
              key={`key${year.suffix}`}
              className={`percent-bar ${_yClass(year)}`}
            >
              <span className="swatch" /> {year.label}
              {hasNoData(year) ? " (N/A)" : ""}
            </li>
          ))}
          {showBenchmarks ? (
            <li className="benchmark-line">
              <span className="swatch" /> {survey.short_benchmark_name}
            </li>
          ) : null}
        </ul>
      ) : null}
      {comparisonObject ? (
        <ul className="legend">
          <li className="percent-bar">
            <span className="swatch" /> Overall
          </li>
          <li className="percent-bar comparison">
            <span className="swatch" /> For students with{" "}
            {comparisonObject.name}
          </li>
        </ul>
      ) : null}
    </div>
  );
};

// NOTE: tried react-bootstrap Accordion+Panel but it broke styling and didn't handle header clicks the same way!
class SurveyPanel extends React.PureComponent {
  state = { fullHistory: false };

  render() {
    let {
      item,
      comparisonObject,
      survey,
      activeView,
      showBenchmarks,
      showHistory,
      showDots,
      expanded,
      onToggle,
      newCultivateStyle,
    } = this.props;
    let { fullHistory } = this.state;

    let years;
    if (fullHistory) {
      years = survey.list.slice(0).reverse();
    } else if (activeView === "performance") {
      years = [survey._mainYear];
    } else if (survey._schoolYears) {
      // for new BEL, show "trends" only within most recent year
      years = survey._schoolYears[survey._schoolYears.length - 1];
    } else {
      years = survey._fullYears.slice(-2);
    }

    let _minNumSurveysToShowToggle = activeView === "performance" ? 1 : 2;
    let showToggle =
      showHistory && survey.list.length > _minNumSurveysToShowToggle;
    let showLegend = showHistory || newCultivateStyle;
    let numLitDots = item.data.diff_led || 0;

    return (
      <a
        className={`row-style ${expanded ? "" : "collapsed"}`}
        role="button"
        aria-expanded={expanded ? "true" : "false"}
        aria-controls={"itemDetails-" + item.slug}
        onClick={(_) => onToggle()}
      >
        {item._xtraHeading && (
          <span className="heading">{item._xtraHeading}: </span>
        )}
        <span className="title">{item.prompt}</span>
        {showDots ? (
          <span className="dots">
            {[0, 1, 2, 3, 4].map((n) => (
              <span
                key={n}
                className={`dot ${n < numLitDots ? "lit" : "dim"}`}
              />
            ))}
          </span>
        ) : null}
        <GlyphCollapseToggle />
        <Collapse
          in={expanded}
          onEntered={(el) => $(el).trigger("shown.inq.collapse")}
          onExited={(el) => $(el).trigger("hidden.inq.collapse")}
        >
          <div id={"itemDetails-" + item.slug}>
            <FrequencyGraph
              {...{
                item,
                comparisonObject,
                survey,
                years,
                showLegend,
                showBenchmarks,
                newCultivateStyle,
              }}
            />
            {showToggle ? (
              <button
                className="btn toggle-history"
                onClick={(evt) => {
                  evt.stopPropagation();
                  this.setState({ fullHistory: !fullHistory });
                }}
              >
                {fullHistory ? "Hide" : "Show"} Full History
              </button>
            ) : null}
          </div>
        </Collapse>
      </a>
    );
  }
}

export class SurveyAccordion extends React.PureComponent {
  render() {
    let {
      activeObject,
      comparisonObject,
      childObjects,
      survey,
      activeView,
      showBenchmarks,
      expandedItems,
      updateItems,
      showHistory = true,
      showDots = false,
      StemEl = "h3",
      stemClasses = null,
      newCultivateStyle = false,
    } = this.props;

    let slugs = reduceToObject(expandedItems, (s) => [s, true]);
    let completelyExpanded = childObjects.every((d) => slugs[d.slug]);
    let toggle = (s) => {
      if (s in slugs) updateItems(expandedItems.filter((es) => es !== s));
      else updateItems([...expandedItems, s]);
    };

    return (
      <div className="survey-accordion">
        <StemEl className={stemClasses}>{activeObject.stem}</StemEl>
        {completelyExpanded ? (
          <a
            href="#"
            className="expand-toggle"
            onClick={(evt) => {
              evt.preventDefault();
              updateItems([]);
            }}
          >
            Collapse All
          </a>
        ) : (
          <a
            href="#"
            className="expand-toggle"
            onClick={(evt) => {
              evt.preventDefault();
              updateItems(childObjects.map((d) => d.slug));
            }}
          >
            Expand All
          </a>
        )}
        <div className="collapsable-group">
          {childObjects.map((d) => (
            <SurveyPanel
              key={d.slug}
              expanded={Boolean(slugs[d.slug])}
              onToggle={(_) => toggle(d.slug)}
              {...{
                item: d,
                comparisonObject,
                survey,
                activeView,
                showBenchmarks,
                showHistory,
                showDots,
                newCultivateStyle,
              }}
            />
          ))}
        </div>
      </div>
    );
  }
}

class ResourcePanel extends React.PureComponent {
  state = { expanded: false };

  render() {
    let { resource } = this.props;
    let { expanded } = this.state;

    return (
      <div
        className={`row-style ${expanded ? "" : "collapsed"}`}
        role="button"
        aria-expanded={expanded ? "true" : "false"}
        aria-controls={"resourceDetails-" + resource.id}
        onClick={(evt) => this.setState({ expanded: !expanded })}
      >
        <span className="title">{resource.name}</span>
        <GlyphCollapseToggle />
        <Collapse
          in={expanded}
          onEntered={(el) => $(el).trigger("shown.inq.collapse")}
          onExited={(el) => $(el).trigger("hidden.inq.collapse")}
        >
          <div id={"resourceDetails-" + resource.id}>
            <p className="description">{resource.description}</p>
            <span className="source">{resource.source}</span>
            {resource.url ? (
              <a className="small-square-blue-btn" href={resource.url}>
                Download
              </a>
            ) : (
              <a className="small-square-blue-btn disabled">Login Required</a>
            )}
          </div>
        </Collapse>
      </div>
    );
  }
}

export let ResourcesAccordion = ({ activeObject }) => (
  <div className="resources-accordion">
    <h3>Resources for this Measure</h3>
    <div className="collapsable-group measure-resources">
      {activeObject._resources.length ? (
        activeObject._resources.map((d) => (
          <ResourcePanel key={d.id} resource={d} />
        ))
      ) : (
        <p className="empty">None available</p>
      )}
    </div>
  </div>
);

const ITEMS_LOADING = "loading";
const ITEMS_FAILED = "error";
const ITEMS_READY = "ready";

export class AsyncAccordions extends React.PureComponent {
  updateStatus(measure) {
    const { links, _measureStatus, _processAsyncItems, forceUpdate } =
      this.props;

    let reupdateCarefully = (status) => {
      // we always want to track per-measure status
      _measureStatus[measure.slug] = status;
      // but our own display may have already changed
      let { activeObject } = this.props;
      if (activeObject === measure) {
        this.setState({ status });
        if (status === ITEMS_READY) forceUpdate();
      }
    };

    let { slug } = measure;
    let status = _measureStatus[slug];
    switch (status) {
      case ITEMS_READY:
      case ITEMS_LOADING:
      case ITEMS_FAILED:
        break;
      case undefined: // has not been seen before
        status = _measureStatus[slug] = ITEMS_LOADING;
        $.ajax(links.items_api + `?m=${slug}`).then(
          (result) => {
            _processAsyncItems(measure, result.items, result.resources);
            reupdateCarefully(ITEMS_READY);
          },
          (error) => {
            reupdateCarefully(ITEMS_FAILED);
          },
        );
    }
    this.setState({ status });
  }

  handleRetry = (evt) => {
    let { activeObject, _measureStatus } = this.props;
    delete _measureStatus[activeObject.slug];
    this.updateStatus(activeObject);
  };

  componentWillMount() {
    let { activeObject } = this.props;
    this.updateStatus(activeObject);
  }

  componentWillReceiveProps(nextProps) {
    let { activeObject } = nextProps;
    this.updateStatus(activeObject);
  }

  render() {
    let {
      survey,
      target,
      activeView,
      expandedItems,
      updateItems,
      activeObject,
      childObjects,
      displayMode = "normal",
    } = this.props;
    let { status } = this.state;
    switch (status) {
      case ITEMS_READY:
        return displayMode === "mobile-summary" ? (
          <ul className="item-list">
            {childObjects.map((d) => (
              <li key={d.slug}>{d.prompt}</li>
            ))}
          </ul>
        ) : (
          <div>
            <SurveyAccordion
              {...{
                activeObject,
                childObjects,
                survey,
                activeView,
                expandedItems,
              }}
              showBenchmarks={!target.is_group}
              updateItems={updateItems}
            />
            {activeObject._resources.length ? (
              <ResourcesAccordion activeObject={activeObject} />
            ) : null}
          </div>
        );
      case ITEMS_LOADING:
        return <div className="status-message loading">Loading details…</div>;
      case ITEMS_FAILED:
        return (
          <div className="status-message error">
            Error fetching details!{" "}
            <Button onClick={this.handleRetry}>Retry</Button>
          </div>
        );
    }
  }
}

export class AjaxForm extends React.PureComponent {
  static Submit = ({ ready, className = "", children, _form }) => (
    <label>
      <button className={`btn ${className}`} disabled={!_form.enabled}>
        {children}
      </button>{" "}
      {_form.statusMessage}
    </label>
  );

  state = { saveStatus: null };

  static formData(form) {
    // we assume only one value per field name
    let data = {};
    let rawForm = new FormData(form);
    if (rawForm.entries) for (let [k, v] of rawForm.entries()) data[k] = v;
    else
      for (let field of form.querySelectorAll("input,textarea"))
        data[field.name] = field.value;
    return data;
  }

  handleFormSubmit = (evt) => {
    evt.preventDefault();

    let data = this.constructor.formData(evt.target);

    let {
      action: method = "post",
      target: url,
      user,
      onSuccessfulSave,
      onLoginNeeded,
    } = this.props;
    this.setState({ saveStatus: "pending" });
    $.ajax(url, {
      type: method,
      data: data,
      success: (respData) => {
        this.setState({ saveStatus: null });
        if (onSuccessfulSave) onSuccessfulSave(respData);
      },
      error: (jqXHR) => {
        this.setState({ saveStatus: "retry" });
        if (jqXHR.status === 403) {
          if (onLoginNeeded) onLoginNeeded();
        }
      },
    });
  };

  handleFieldInput = (evt) => {
    let field = evt.target;
    let { onDataChange } = this.props;
    if (onDataChange) onDataChange({ [field.name]: field.value });
  };

  componentDidMount() {
    let form = findDOMNode(this);
    for (let field of form.querySelectorAll("input,textarea"))
      field.addEventListener("input", this.handleFieldInput, false);
  }

  componentWillUnmount() {
    let form = findDOMNode(this);
    for (let field of form.querySelectorAll("input,textarea"))
      field.removeEventListener("input", this.handleFieldInput, false);
  }

  render() {
    let { action, target, children } = this.props;
    let { saveStatus } = this.state;

    let getSaveEnabled = (ready) => ready && saveStatus !== "pending";
    let statusMessage = {
      pending: "Saving changes, please wait…",
      retry: "Saving failed, please try again!",
    }[saveStatus];

    let _children = React.Children.map(children, (child) =>
      child && child.type === this.constructor.Submit
        ? React.cloneElement(
            child,
            {
              _form: {
                enabled: getSaveEnabled(child.props.ready),
                statusMessage,
              },
            },
            saveStatus === "retry" ? "Retry" : child.props.children,
          )
        : child,
    );

    return (
      <form action={action} target={target} onSubmit={this.handleFormSubmit}>
        {_children}
      </form>
    );
  }
}

export class LoginModal extends React.PureComponent {
  state = { recoveryFailed: false, badCredentials: false };

  dismissModal = () => {
    let { onSuccess } = this.props;
    if (onSuccess) onSuccess();
  };

  handleFormSubmit = (evt) => {
    evt.preventDefault();

    let { links } = this.props;

    let handleFailure = () => {
      this.setState({ recoveryFailed: true });
    };

    let form = evt.target;
    let loginFields = {
      //csrfmiddlewaretoken: getCsrfToken(),
      username: form.elements["username"].value,
      password: form.elements["password"].value,
    };
    $.ajax({
      url: links.notes_api + "?fixup_login",
      type: "post",
      data: loginFields,
    }).then((result) => {
      if (result === "OK") {
        this.dismissModal();
      } else {
        this.setState({ badCredentials: true });
      }
    }, handleFailure);
  };

  render() {
    let { user, links } = this.props;
    let { recoveryFailed, badCredentials } = this.state;

    return (
      <Modal show={true} onHide={this.dismissModal}>
        <Modal.Header closeButton={recoveryFailed}>
          <Modal.Title componentClass="h2">Login Required</Modal.Title>
        </Modal.Header>
        {recoveryFailed ? (
          <Modal.Body>
            Something went wrong. Please use the{" "}
            <a target="_blank" href={links.login}>
              login page
            </a>{" "}
            to sign in before closing this dialog. If you are still unable to
            save changes, try refreshing this page.
          </Modal.Body>
        ) : (
          <Modal.Body>
            You have been logged out. In order to save changes, please sign in.
            <form onSubmit={this.handleFormSubmit}>
              <label>
                {user.email ? "Email" : "Username"}:{" "}
                <input
                  name="username"
                  defaultValue={user.email || user.username}
                  disabled
                />
              </label>
              <br />
              <label>
                Password: <input name="password" type="password" />
              </label>{" "}
              {badCredentials ? (
                <span className="text-danger">Password incorrect.</span>
              ) : null}
              <br />
              <button>Login</button>
            </form>
          </Modal.Body>
        )}
      </Modal>
    );
  }
}

export let UnshareButton = ({
  measureSlug,
  onSuccess,
  onLoginNeeded,
  links,
}) => (
  <a
    href="#"
    className="unshare-measure"
    onClick={(evt) => {
      evt.preventDefault();
      let ok = confirm(
        "Discard your responses and stop sharing? Action plans will be kept, but your reflection here will not.",
      );
      if (!ok) return;

      $.ajax(links.notes_api + `?m=${measureSlug}&a=share&t=analysis`, {
        type: "delete",
        success: onSuccess,
        error: (jqXHR) => {
          alert(
            "Couldn't delete this reflection, due to a server or network error. Please try again.",
          );
          if (jqXHR.status === 403) onLoginNeeded(true);
        },
      });
    }}
  >
    Stop sharing this measure
  </a>
);

export let InlineNote = ({ WRAPPER, NC, links, note, measure }) => (
  <WRAPPER className="note-item">
    <a href={noteContextUrl(NC, links, note, measure)}>
      From {noteContextLabel(NC, note, measure)}:
    </a>
    {note.observation}
    <span className="when-modified">
      Last modified:{" "}
      <span className="timestamp">{verboseTimestamp(note.when_modified)}</span>
    </span>
  </WRAPPER>
);

export class NoteShareModal extends React.PureComponent {
  state = { saveStatus: null };

  dismissModal = () => {
    let {
      modalInfo: { analysis },
    } = this.props;
    let { onDataChange, onClose } = this.props;

    if (analysis._modified) {
      let ok = confirm(
        "Your changes will be lost if you close this panel before sharing.",
      );
      if (!ok) return;
      else onDataChange(analysis._original);
    }
    onClose();
  };

  render() {
    let {
      modalInfo: { NC, measure, analysis, notes },
      user,
      links,
    } = this.props;
    let { onDataChange, onSuccessfulSave, onClose, onLoginNeeded } = this.props;

    let { needs_growth, rationale } = analysis;

    return (
      <Modal
        show={true}
        backdrop={"static"}
        className="measure-card"
        onHide={this.dismissModal}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title componentClass="h2">{measure.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label>What do you observe about this measure?</label>
          <ul className="measure-notes">
            {notes.list.length ? (
              notes.list.map((note) => (
                <InlineNote
                  WRAPPER="li"
                  key={note.context}
                  {...{ NC, links, note, measure }}
                />
              ))
            ) : (
              <li className="empty">
                All notes for this measure are now empty.
              </li>
            )}
          </ul>
          <div className="row">
            <div className="col-md-6">
              <button
                type="button"
                className={`btn measure-type ${
                  needs_growth === true ? "active" : ""
                }`}
                onClick={(evt) => onDataChange({ needs_growth: true })}
              >
                Measure for Growth
              </button>
            </div>
            <div className="col-md-6">
              <button
                type="button"
                className={`btn measure-type ${
                  needs_growth === false ? "active" : ""
                }`}
                onClick={(evt) => onDataChange({ needs_growth: false })}
              >
                Measure of Strength
              </button>
            </div>
          </div>

          <AjaxForm
            user={user}
            target={links.notes_api + `?m=${measure.slug}&a=share`}
            {...{ onDataChange, onSuccessfulSave, onLoginNeeded }}
          >
            <label>
              Why do you want to discuss this measure (high score, positive
              trend, important topic, other reason)?
            </label>
            <textarea name="rationale" defaultValue={rationale} />
            <input
              type="hidden"
              name="needs_growth"
              value={needs_growth || ""}
              onChange={(evt) => null}
            />
            <AjaxForm.Submit
              ready={analysis._modified && needs_growth != null && rationale}
            >
              Share
            </AjaxForm.Submit>
          </AjaxForm>

          {!analysis._placeholder ? (
            <div className="extra-actions">
              <a
                href={
                  links.measure_analysis +
                  `#${analysis.needs_growth ? "growth" : "strength"}?measure=${
                    measure.slug
                  }`
                }
                className="view-shared"
              >
                View on Measure Analysis Page
              </a>
              <UnshareButton
                measureSlug={measure.slug}
                onSuccess={(_) => {
                  // ~HACK: reset this object so it looks like an untouched placeholder again.
                  onDataChange({
                    _original: null,
                    _modified: false,
                    _placeholder: true,
                    needs_growth: null,
                    rationale: "",
                  });
                  onClose();
                }}
                {...{ onLoginNeeded, links }}
              />
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    );
  }
}

export class AbstractTrackURL extends React.PureComponent {
  settingState = false;
  _prevGaUrl = null;

  static currentUrlParts() {
    const splitParams = (params) =>
      params
        .split("&")
        .filter(Boolean)
        .reduce((d, pair) => {
          let [k, v] = pair.split("=");
          d[k] = v;
          return d;
        }, {});

    let { basePathname = "/" } = this;
    let basePath = basePathname.split("/").slice(1, -1);

    let { pathname, search, hash } = window.location;
    // TODO: fully review URI encoding issues (see `decodeURI` WORKAROUND below, `joinParams`, etc.)
    let [_path, _params, _fragment] = [
      pathname.slice(1),
      search.slice(1),
      hash.slice(1),
    ];
    let [_fragPath, _fragParams = ""] = decodeURI(_fragment).split("?"); // WORKAROUND: sometimes e.g. `subject[2]` gets encoded

    let [path, params] = [
      _path.split("/").slice(basePath.length),
      splitParams(_params),
    ];
    let [fragPath, fragParams] = [
      _fragPath.split("/"),
      splitParams(_fragParams),
    ];

    return { path, fragPath, params, fragParams };
  }

  static intializeState(ctx) {
    this.prototype.updateAppState.call(null, this.currentUrlParts(), ctx);
  }

  handleNavigation = (evt) => {
    this.settingState = true;
    this.updateAppState(this.constructor.currentUrlParts());
    this.settingState = false;
  };

  updateUrl() {
    const joinParams = (params) =>
      Object.keys(params)
        .map((k) => `${k}=${params[k]}`)
        .join("&");

    let { basePathname = "/" } = this.constructor;
    let basePath = basePathname.split("/").slice(1, -1);

    let {
      path,
      fragPath = [],
      params,
      fragParams = {},
      _forceHistory,
    } = this.generateUrlParts();
    let { pathname: _wPath, search: _wParams } = window.location;

    var url = "/";
    url += path ? [...basePath, ...path].join("/") : _wPath.slice(1);
    let _params = params ? joinParams(params) : _wParams.slice(1);
    if (_params) url += "?" + _params;

    let newPageUrl = url; // this portion of `url` is used later

    let _frag = fragPath.join("/");
    let _fragParams = joinParams(fragParams);
    if (_fragParams) _frag += "?" + _fragParams;
    if (_frag) url += "#" + _frag;

    let oldPageUrl = _wPath + _wParams;
    if (newPageUrl !== oldPageUrl || _forceHistory)
      history.pushState(null, "", url);
    else history.replaceState(null, "", url);

    // ~HACK: update Google Analytics with page state
    if (window.ga || window._gaq) {
      // normalize any filter usage details to simply "ACTIVE"
      let gaUrl = url; // we could do some processing here
      if (window.ga) {
        ga("set", "page", gaUrl);
        if (gaUrl !== this._prevGaUrl) ga("send", "pageview");
      } else {
        _gaq.push(["_trackPageview", gaUrl]);
      }
      this._prevGaUrl = gaUrl;
    }

    // ~HACK: update login URL too, so it will return to current state.
    let templatedLinks = document.querySelectorAll("a[data-href_template]");
    Array.from(templatedLinks).forEach((linkEl) => {
      let newNext = encodeURIComponent(url),
        hrefTemplate = linkEl.getAttribute("data-href_template");
      linkEl.href = hrefTemplate.replace("_PLACEHOLDER_", newNext);
    });
  }

  // subclasses should override these!
  generateUrlParts() {
    return this.constructor.currentUrlParts();
  }
  updateAppState(urlParts, initialCtx) {}
  // (basic no-ops have been provided in lieu of truly abstract implementation)

  componentWillMount() {
    this.updateUrl(this.props);
    window.addEventListener("popstate", this.handleNavigation, false);
  }

  componentWillUnmount() {
    window.removeEventListener("popstate", this.handleNavigation, false);
  }

  componentDidUpdate() {
    if (!this.settingState) this.updateUrl();
  }

  render() {
    return null;
  }
}
